import { DatePicker } from "antd"
import { Link, useNavigate } from "react-router-dom"
import useApi from "../../core/customhook/useApi"
import Loader from "../../core/common/selfLoder"
import dayjs from "dayjs"
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react"
import Swal from "sweetalert2"
import CollapseHeader from "../../core/common/collapse-header/collapse-header"

interface EMPLOYEE_DATA {
    employeeId: string
    emailId: string
    DOJ: string
    employeeName: string
    gender: string
    department: string
    designation: string
    location: string
    status: string
    DOB: string
    band: string
}

interface FormData {
    ResignationDate: string
    RequestedRelievingDate: string
    RequestedRelievingDateAsPerPolicy: string
    NoticePeriodRequestDays: string
    NoticePeriodDifference: string
    Reasons: string
    Remarks: string
    Status: string
    offboardingDocuments: File[]
}
const Addresignation = () => {

    const {
        data: employeeDetails,
        loading: employeeLoading,
        error: employeeError
    } = useApi('/api/resignation-user', {
        method: "GET",
        autoCall: true,
    })

    const navigate = useNavigate()

    const apiResponse = employeeDetails?.data;
    const employeeData: EMPLOYEE_DATA = {
        employeeId: apiResponse?.employee_id,
        emailId: apiResponse?.email_id,
        DOJ: apiResponse?.join_date,
        employeeName: apiResponse?.employee_name,
        gender: "",
        department: apiResponse?.department,
        designation: apiResponse?.designation,
        location: apiResponse?.work_location,
        status: apiResponse?.status,
        DOB: apiResponse?.date_of_birth,
        band: apiResponse?.band
    };

    const [formData, setFormData] = useState<FormData>({
        ResignationDate: "",
        RequestedRelievingDate: "",
        RequestedRelievingDateAsPerPolicy: "",
        NoticePeriodRequestDays: "",
        NoticePeriodDifference: "",
        Reasons: "",
        Remarks: "",
        Status: "",
        offboardingDocuments: [] as File[],
    });

    // API call for submitting resignation
    const { loading: postLoading, callApi: postApi } = useApi(`/api/add-resignation`, {
        method: "POST",
        autoCall: false,
    });

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        setFormData(prev => ({
            ...prev,
            offboardingDocuments: [
                ...prev.offboardingDocuments,
                ...Array.from(files).filter(
                    file => !prev.offboardingDocuments.some(f => f.name === file.name)
                )
            ]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to submit Off Borading request ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, submit",
            cancelButtonText: "Cancel"
        });

        // If user cancels
        if (!result.isConfirmed) return;


        if (!formData.ResignationDate || !formData.RequestedRelievingDate) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Resignation date and relieving date are required."
            });
            return;
        }

        // Create FormData for submission
        const submissionData = new FormData();
        submissionData.append("resignation_date", dayjs(formData.ResignationDate, "DD-MM-YYYY").format("YYYY-MM-DD"));
        submissionData.append("requested_relieving_date", dayjs(formData.RequestedRelievingDate, "DD-MM-YYYY").format("YYYY-MM-DD"));
        submissionData.append("relieving_date_as_per_policy", dayjs(formData.RequestedRelievingDateAsPerPolicy, "DD-MM-YYYY").format("YYYY-MM-DD"));
        submissionData.append("notice_period_requested_days", formData.NoticePeriodRequestDays ?? "");
        submissionData.append("reason", formData.Reasons);
        submissionData.append("remarks", formData.Remarks);

        // Append all files
        formData.offboardingDocuments.forEach(file => {
            submissionData.append("files", file);
        });


        try {
            const response = await postApi(submissionData);

            if (response?.success && response.statusCode === 200) {
                Swal.fire({
                    icon: "success",
                    title: "Resignation Submitted",
                    text: response?.message ?? "Resignation added successfully"
                });
                navigate("/offboarding/view-offboarding") // optional navigation
            }
        } catch (error: any) {
            if (error?.response?.status === 409) {
                Swal.fire({
                    icon: "info",
                    title: "Already Submitted",
                    text: error.response.data?.message || "Resignation already exists."
                });
                return;
            }

            if (error?.response?.status === 400) {
                Swal.fire({
                    icon: "warning",
                    title: "Invalid Data",
                    text: error.response.data?.message || "Please check the entered details."
                });
                return;
            }

            Swal.fire({
                icon: "error",
                title: "Submission Failed",
                text: "Something went wrong. Please try again later."
            });

            console.error("Submission error:", error);
        }
    };

    const handleResignationDateChange = (date: Dayjs | null) => {
        if (!date) return;

        const resignationDateStr = date.format("DD-MM-YYYY");
        const resignationDateObj = date;

        // let requestedRelievingPolicy: any;
        // if (['GT', 'M1', 'M2', 'M3', 'M4'].includes(apiResponse?.band)) {
        //     requestedRelievingPolicy = date.add(60, "days");
        // } else {
        //     requestedRelievingPolicy = date.add(90, "days");
        // }

        let requestedRelievingPolicy: any;

        const band = apiResponse?.band;

        if (band === 'T') {
            requestedRelievingPolicy = date.add(0, 'days');
        }
        else if (band === 'GT') {
            requestedRelievingPolicy = date.add(60, 'days');
        }
        else if (band?.startsWith('M')) {
            const level = parseFloat(band.substring(1));

            if (level >= 1 && level <= 4) {
                requestedRelievingPolicy = date.add(60, 'days');
            } else if (level >= 5) {
                requestedRelievingPolicy = date.add(90, 'days');
            }
        }
        else {
            requestedRelievingPolicy = date.add(0, 'days');
        }



        const daysDifference = requestedRelievingPolicy.diff(resignationDateObj, "day");

        setFormData((prev) => ({
            ...prev,
            ResignationDate: resignationDateStr,
            NoticePeriodDifference: daysDifference.toString(),
            RequestedRelievingDateAsPerPolicy: requestedRelievingPolicy.format("DD-MM-YYYY"),
        }));
    };


    const handleRelievingDate = (date: Dayjs | null) => {
        if (!date) return;

        const relievingDateStr = date.format("DD-MM-YYYY");

        const resignationDateObj = dayjs(formData.ResignationDate, "DD-MM-YYYY");
        if (!resignationDateObj.isValid()) return;

        const noticePeriodDays = date.diff(resignationDateObj, "day");

        setFormData((prev) => ({
            ...prev,
            RequestedRelievingDate: relievingDateStr,      // string
            NoticePeriodRequestDays: noticePeriodDays.toString(), // match FormData type
        }));
    };

    useEffect(() => {
        if (employeeDetails?.data) {
            const today = dayjs();
            handleResignationDateChange(today);
            console.log("UseEfect runs")
        }
        console.log("UseEfect mount")
    }, [employeeDetails]);



    return (
        <>
            <Loader loading={employeeLoading} />
            <div className="page-wrapper">
                <div className="content">

                    {/* Page Header */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Offboarding Application</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to="/">
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Offboarding</li>
                                    <li className="breadcrumb-item active">
                                        Offboarding Form
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>

                    {/* Main Card */}
                    <div className="card">
                        <form onSubmit={handleSubmit}>
                            <div className="card-header w-full d-flex align-items-center justify-content-between mb-3">
                                {/* Left: Heading */}
                                <h5 className="mb-0">Offboarding Form</h5>
                            </div>


                            <div className="row">
                                <div className="row m-1">
                                    {/* Employee Details */}

                                    <div className="col-md-6">
                                        <div className="card h-100">
                                            <div className="card-header d-flex align-items-center justify-content-between">
                                                <h5 className="mb-0">Employee Details</h5>
                                            </div>

                                            <div className="card-body">
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Employee Name</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter employee name"
                                                            value={employeeData.employeeName || ""}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Employee ID</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="EMP001"
                                                            value={employeeData.employeeId || ""}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Date of Birth</label>
                                                    <div className="col-8">
                                                        <input
                                                            className="form-control datetimepicker"
                                                            value={
                                                                employeeData.DOB
                                                                    ? dayjs(employeeData.DOB, "DD-MM-YYYY").format("DD MMMM YYYY")
                                                                    : dayjs().format("DD MMMM YYYY")
                                                            }
                                                            disabled
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Date of Joining</label>
                                                    <div className="col-8">
                                                        <input
                                                            className="form-control datetimepicker"
                                                            value={
                                                                employeeData.DOJ
                                                                    ? dayjs(employeeData.DOJ, "DD-MM-YYYY").format("DD MMMM YYYY")
                                                                    : dayjs().format("DD MMMM YYYY")}
                                                            readOnly
                                                            disabled
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Department</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder=""
                                                            value={employeeData.department || ""}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Designation</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder=""
                                                            value={employeeData.designation || ""}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Work Location</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder=""
                                                            value={employeeData.location || ""}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Grade</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder=""
                                                            value={employeeData.band || ""}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Resignation Details */}
                                    <div className="col-md-6">
                                        <div className="card h-100">
                                            <div className="card-header d-flex align-items-center justify-content-between">
                                                <h5 className="mb-0">Offboarding Details</h5>
                                            </div>

                                            <div className="card-body">
                                                {/* Resignation Date */}
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Resignation Date</label>
                                                    <div className="col-8">
                                                        <input
                                                            className="form-control datetimepicker"
                                                            value={
                                                                formData.ResignationDate
                                                                    ? dayjs(formData.ResignationDate, "DD-MM-YYYY").format("DD MMMM YYYY") // Format date for display
                                                                    : dayjs().format("DD MMMM YYYY") // Show current date if no resignation date is set
                                                            }
                                                            placeholder="DD-MMMM-YY"
                                                            required
                                                            disabled // Disables the input field (prevents changes)
                                                        />
                                                    </div>
                                                </div>

                                                {/* Relieving Date Requested */}
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Relieving Date Requested
                                                        <sup className="text-danger">*</sup>
                                                    </label>
                                                    <div className="col-8">
                                                        <DatePicker
                                                            className="form-control datetimepicker"
                                                            format="DD MMMM YYYY"
                                                            value={
                                                                formData.RequestedRelievingDate
                                                                    ? dayjs(formData.RequestedRelievingDate, "DD-MM-YYYY")
                                                                    : null
                                                            }
                                                            onChange={handleRelievingDate}
                                                            placeholder="DD MM YYYY"
                                                            disabledDate={(current) =>
                                                                current && current < dayjs().startOf("day")
                                                            }
                                                            required
                                                        />

                                                    </div>
                                                </div>

                                                {/* Notice Period Requested */}
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Notice Period Requested
                                                        <sup className="text-danger">*</sup>
                                                    </label>
                                                    <div className="col-8">
                                                        <input
                                                            className="form-control"
                                                            type="text"  // Ensure the input type is text, for better handling of numbers
                                                            value={formData?.NoticePeriodRequestDays ?? ""} // Otherwise, use the actual value
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                                {/* Notice Period Difference Days */}
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Notice Period Difference Days</label>
                                                    <div className="col-8">
                                                        <input type="text"
                                                            className="form-control"
                                                            value={formData?.NoticePeriodDifference ?? ""}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>

                                                {/* Relieving Date As Per Policy */}
                                                <div className="row mb-3 align-items-center">
                                                    <label className="col-4 col-form-label text-end">Relieving Date As Per Policy</label>
                                                    <div className="col-8">
                                                        {/* {isClient && ( */}
                                                        <DatePicker
                                                            className="form-control datetimepicker"
                                                            format="DD MMMM YYYY"
                                                            value={
                                                                formData.RequestedRelievingDateAsPerPolicy
                                                                    ? dayjs(formData.RequestedRelievingDateAsPerPolicy, "DD-MM-YYYY")
                                                                    : null
                                                            }
                                                            readOnly
                                                            placeholder="DD MM YYYY"
                                                        />
                                                        {/* )} */}
                                                    </div>
                                                </div>

                                                {/* Reasons */}
                                                <div className="row mb-3 align-items-start">
                                                    <label className="col-4 col-form-label text-end">Reasons
                                                        <sup className="text-danger">*</sup>
                                                    </label>
                                                    <div className="col-8">
                                                        <select
                                                            className="form-control"
                                                            value={formData.Reasons ?? ""}
                                                            onChange={(e) =>
                                                                setFormData((prev) => ({
                                                                    ...prev,
                                                                    Reasons: e.target.value,
                                                                }))
                                                            }
                                                            required
                                                        >
                                                            <option value="" disabled>Select a Reason</option>
                                                            <option value="projectClosure">Project Closure</option>
                                                            <option value="performanceIssues">Performance Issues</option>
                                                            <option value="unauthorizedAbsence">Unauthorized Absence from work</option>
                                                            <option value="personalReasons">Personal Reasons</option>
                                                            <option value="termination">Termination</option>
                                                            <option value="retirement">Retirement</option>
                                                            <option value="healthIssues">Health Issues</option>
                                                            <option value="codeOfConductViolation">Code of Conduct Violation</option>
                                                            <option value="higherStudies">Higher Studies</option>
                                                            <option value="betterJobOpportunity">Better Job Opportunity</option>
                                                            <option value="others">Others (Please provide option to write description)</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="row mb-3 align-items-start">
                                                    <label className="col-4 col-form-label text-end">Remarks
                                                        {/* <sup className="text-danger">*</sup> */}
                                                    </label>
                                                    <div className="col-8">
                                                        <textarea
                                                            className="form-control"
                                                            placeholder="Enter remarks"
                                                            value={formData.Remarks ?? ""}
                                                            onChange={(e) =>
                                                                setFormData((prev) => ({
                                                                    ...prev,
                                                                    Remarks: e.target.value,
                                                                }))
                                                            }
                                                        >
                                                        </textarea>
                                                    </div>
                                                </div>

                                                {/* Upload Documents */}
                                                <div className="row mb-3 align-items-start">
                                                    <label className="col-4 col-form-label text-end">Upload Documents</label>
                                                    <div className="col-8">
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            name="offboardingDocuments"
                                                            multiple
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            onChange={handleFileChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex justify-content-end my-3">
                                <button
                                    type="submit"
                                    className="btn btn-primary custom_button d-flex align-items-center mx-2"
                                >
                                    {" "}
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div >

                {/* Footer */}
                < div className="footer d-flex align-items-center justify-content-center bg-white border-top p-3" >
                    <p className="mb-0"> © {new Date().getFullYear()} WeConnect. All rights reserved.</p>
                </div >
            </div >
        </>
    )
}

export default Addresignation